import { useEffect, useRef, useState } from "react";
import { InvarDataAccess } from "../../data/InvarDataAccess";
import { BrokerageResponse } from "../../data/InvarDataModels/BrokerageResponse";
import { ConveyanceResponse } from "../../data/InvarDataModels/ConveyanceResponse";
import {
  Mondrian,
  MondrianColumn,
  MondrianInputObject,
} from "@spt/mondrian/exports";
import { MachineResponse } from "../../data/InvarDataModels/MachineResponse";
import { BrokerResponse } from "../../data/InvarDataModels/BrokerResponse";
import { MonitoredWorkOrderResponse } from "../../data/InvarDataModels/MonitoredWorkOrderResponse";
import {
  FilterColumnSettings,
  MondrianSettings,
} from "@spt/mondrian/src/Components/Structs/Interfaces/MondrianSettings";
import WorkOrderTimeline from "../../components/WorkOrderTimeline";
import { MonitoredWorkOrderDatumResponse } from "../../data/InvarDataModels/MonitoredWorkOrderDatumResponse";
import dayjs from "dayjs";

interface IProps {
  IDA: InvarDataAccess;
  Environment: NonNullable<"LIVE" | "TEST" | "DEV" | "LOCAL" | "">;
}

export const InvarActivity = (props: IProps) => {
  const IDA = props.IDA;

  const [_CreateConveyanceName, setCreateConveyanceName] = useState("");

  const [TheseBrokerages, setTheseBrokerages] = useState<
    Array<BrokerageResponse> | undefined
  >(undefined);
  const [BrokeragesLoading, setBrokeragesLoading] = useState(true);
  const [BrokerageLoadingMap, setBrokerageLoadingMap] = useState<
    Array<boolean> | undefined
  >(undefined);

  const [TheseBrokers, setTheseBrokers] = useState<
    Array<BrokerResponse> | undefined
  >(undefined);
  const [BrokersLoading, setBrokersLoading] = useState(true);
  const [BrokerLoadingMap, setBrokerLoadingMap] = useState<
    Array<boolean> | undefined
  >(undefined);

  const [TheseWorkOrders, setTheseWorkOrders] = useState<
    Array<MonitoredWorkOrderResponse> | undefined
  >(undefined);
  const [WorkOrdersLoading, setWorkOrdersLoading] = useState(true);
  const [WorkOrderLoadingMap, setWorkOrderLoadingMap] = useState<
    Array<boolean> | undefined
  >(undefined);

  const [TheseWorkOrderData, setTheseWorkOrderData] = useState<
    Array<MonitoredWorkOrderDatumResponse> | undefined
  >(undefined);
  const [WorkOrderDataLoading, setWorkOrderDataLoading] = useState(true);
  const [WorkOrderDataLoadingMap, setWorkOrderDataLoadingMap] = useState<
    Array<boolean> | undefined
  >(undefined);

  const [TheseConveyances, setTheseConveyances] = useState<
    Array<ConveyanceResponse> | undefined
  >(undefined);
  const [ConveyancesLoading, setConveyancesLoading] = useState(true);
  const [ConveyanceLoadingMap, setConveyanceLoadingMap] = useState<
    Array<boolean> | undefined
  >(undefined);

  const [TheseMachines, setTheseMachines] = useState<
    Array<MachineResponse> | undefined
  >(undefined);
  const [MachinesLoading, setMachinesLoading] = useState(true);
  const [MachineLoadingMap, setMachineLoadingMap] = useState<
    Array<boolean> | undefined
  >(undefined);

  const [StructuredConveyances, setStructuredConveyances] = useState<
    Array<MondrianInputObject> | undefined
  >(undefined);

  // allows us to inject a filter into Mondrian from external controls
  const [MondrianFilterDefaults, SetMondrianFilterDefaults] =
    useState<FilterColumnSettings>(undefined);
  const [PassInSettings, SetPassInSettings] =
    useState<MondrianSettings>(undefined);
  const [RenderKey, SetRenderKey] = useState("");

  useEffect(() => {
    SetPassInSettings({
      DisplaySettings: { DisplayColumnHeaders: true },
      PagerSettings: { ResultsPerPage: 100 },
      FilterSettings:
        MondrianFilterDefaults === undefined
          ? {}
          : {
              Defaults: [MondrianFilterDefaults],
            },
      GroupAndSortSettings: {},
    });

    SetRenderKey(Math.random().toString());
  }, [MondrianFilterDefaults]);

  useEffect(() => {
    if (TheseBrokers !== undefined) {
      setBrokersLoading(false);
      let NewBrokerLoadingMap = [];
      TheseBrokers.forEach((broker) => {
        NewBrokerLoadingMap[broker.BrokerID] = false;
      });
      setBrokerLoadingMap(NewBrokerLoadingMap);
    }
  }, [TheseBrokers]);

  useEffect(() => {
    if (TheseWorkOrders !== undefined) {
      setWorkOrdersLoading(false);
      let NewWorkOrderLoadingMap = [];
      TheseWorkOrders.forEach((workorder) => {
        NewWorkOrderLoadingMap[workorder.WorkOrderID] = false;
      });
      setWorkOrderLoadingMap(NewWorkOrderLoadingMap);
    }
  }, [TheseWorkOrders]);

  useEffect(() => {
    if (TheseWorkOrderData !== undefined) {
      setWorkOrderDataLoading(false);
      let NewWorkOrderDataLoadingMap = [];
      TheseWorkOrderData.forEach((workorderdatum) => {
        NewWorkOrderDataLoadingMap[workorderdatum.WorkOrderID] = false;
      });
      setWorkOrderDataLoadingMap(NewWorkOrderDataLoadingMap);
    }
  }, [TheseWorkOrderData]);

  useEffect(() => {
    if (TheseMachines !== undefined) {
      setMachinesLoading(false);
      let NewMachineLoadingMap = [];
      TheseMachines.forEach((machine) => {
        NewMachineLoadingMap[machine.MachineID] = false;
      });
      setMachineLoadingMap(NewMachineLoadingMap);
    }
  }, [TheseMachines]);

  useEffect(() => {
    if (TheseBrokerages !== undefined) {
      setBrokeragesLoading(false);
      let NewBrokerageLoadingMap = [];
      TheseBrokerages.forEach((brokerage) => {
        NewBrokerageLoadingMap[brokerage.BrokerageID] = false;
      });
      setBrokerageLoadingMap(NewBrokerageLoadingMap);
    }
  }, [TheseBrokerages]);

  useEffect(() => {
    if (TheseConveyances !== undefined) {
      setConveyancesLoading(false);
      let NewConveyanceLoadingMap = [];
      TheseConveyances.forEach((conveyance) => {
        NewConveyanceLoadingMap[conveyance.ConveyanceID] = false;
      });
      setConveyanceLoadingMap(NewConveyanceLoadingMap);
    }
  }, [TheseConveyances]);

  function UpdateBrokerLoadingMap(BrokerID: number, Loading: boolean) {
    let newMap = [...BrokerLoadingMap];
    newMap[BrokerID] = Loading;
    setBrokerLoadingMap(newMap);
  }

  function UpdateBrokerageLoadingMap(BrokerageID: number, Loading: boolean) {
    let newMap = [...BrokerageLoadingMap];
    newMap[BrokerageID] = Loading;
    setBrokerageLoadingMap(newMap);
  }

  function UpdateMachineLoadingMap(MachineID: number, Loading: boolean) {
    let newMap = [...MachineLoadingMap];
    newMap[MachineID] = Loading;
    setMachineLoadingMap(newMap);
  }

  function UpdateConveyanceLoadingMap(ConveyanceID: number, Loading: boolean) {
    let newMap = [...ConveyanceLoadingMap];
    newMap[ConveyanceID] = Loading;
    setConveyanceLoadingMap(newMap);
  }

  function GetConveyances() {
    IDA.GetConveyances().then((data) => {
      setTheseConveyances(data);
    });
  }

  function GetBrokerages() {
    IDA.GetBrokerages().then((data) => {
      setTheseBrokerages(data);
    });
  }

  function GetMachines() {
    IDA.GetMachines().then((data) => {
      setTheseMachines(data);
    });
  }

  function GetBrokers() {
    IDA.GetBrokers().then((data) => {
      setTheseBrokers(data);
    });
  }

  function GetWorkOrders() {
    IDA.GetAllActiveWorkOrders().then((data) => {
      setTheseWorkOrders(data);
    });
  }

  function GetWorkOrderData() {
    IDA.GetAllActiveWorkOrderData().then((data) => {
      setTheseWorkOrderData(data);
    });
  }

  const refreshInterval = 5000;

  useEffect(() => {
    if (IDA !== undefined) {
      if (ConveyancesLoading) {
        GetConveyances();
      }
      if (BrokeragesLoading) {
        GetBrokerages();
      }
      if (MachinesLoading) {
        GetMachines();
      }
      if (BrokersLoading) {
        GetBrokers();
      }
      if (WorkOrdersLoading) {
        GetWorkOrders();
      }
      if (WorkOrderDataLoading) {
        GetWorkOrderData();
      }
      const id = setInterval(() => {
        setTimeout(() => {
          GetBrokers();
          GetWorkOrders();
          GetWorkOrderData();
        }, 500);
      }, refreshInterval);
    }
  }, [IDA]);

  const MondrianColumns: MondrianColumn[] = [
    {
      HumanName: "Conveyance",
      MachineName: "Name",
      Sortable: true,
      Groupable: false,
      Filterable: true,
      Hideable: false,
    },
    {
      HumanName: "Machine",
      MachineName: "BMachine",
      Sortable: false,
      Groupable: false,
      Filterable: true,
      Hideable: false,
    },
    {
      HumanName: "Environment",
      MachineName: "BEnvironment",
      Sortable: false,
      Groupable: false,
      Filterable: true,
      Hideable: false,
    },
    {
      HumanName: "Activity",
      MachineName: "BActivity",
      Sortable: true,
      Groupable: false,
      Filterable: false,
      Hideable: false,
    },
    {
      HumanName: "Action Buttons",
      MachineName: "Actions",
      Sortable: false,
      Groupable: false,
      Filterable: false,
      Hideable: false,
    },
    {
      HumanName: "Status",
      MachineName: "Status",
      Sortable: true,
      Groupable: true,
      Filterable: true,
      Hideable: false,
    },
    {
      HumanName: "_ROW_PROCESSING",
      MachineName: "_ROW_PROCESSING",
      Sortable: false,
      Groupable: false,
      Filterable: false,
      Hideable: false,
    },
  ];

  useEffect(() => {
    if (
      TheseConveyances !== undefined &&
      TheseBrokerages !== undefined &&
      TheseBrokers !== undefined &&
      TheseWorkOrders !== undefined
    ) {
      const StructuredConveyances: MondrianInputObject[] = [];

      TheseConveyances.sort((l, r) => {
        return l.ConveyanceID - r.ConveyanceID;
      }).forEach((thisconveyance, i) => {
        TheseBrokerages.filter(
          (brokerage) => brokerage.ConveyanceID === thisconveyance.ConveyanceID,
        )
          .sort((l, r) => {
            return l.MachineName.localeCompare(r.MachineName);
          })
          .forEach((thisbrokerage, j) => {
            let BrokerCount = BrokersLoading
              ? 0
              : TheseBrokers.filter(
                  (ith) =>
                    ith.BrokerageID === thisbrokerage.BrokerageID && ith.Active,
                ).length;

            let thisMachine = TheseMachines.find(
              (th) => th.Name === thisbrokerage.MachineName,
            );

            StructuredConveyances.push({
              Name: thisconveyance.Name,
              BMachine: thisbrokerage.MachineName,
              BEnvironment: thisbrokerage.Environment,
              BActivity: {
                _TYPE: "Element",
                ElementValue: BrokersLoading ? (
                  <div className="LoadingPanel Cell"></div>
                ) : BrokerCount === 0 ? (
                  <span className="NoBrokers">-</span>
                ) : (
                  <ul className="YesBrokers">
                    {TheseBrokers.filter(
                      (ith) =>
                        ith.BrokerageID === thisbrokerage.BrokerageID &&
                        ith.Active,
                    ).map((iith, k) => {
                      return (
                        <li
                          key={
                            "bk_nm_" +
                            thisbrokerage.BrokerageID.toString() +
                            "_" +
                            k.toString()
                          }
                        >
                          {iith.BrokerName.split("[")[0].trim()}
                          <br />
                          {WorkOrdersLoading ? (
                            <div className="LoadingPanel Cell"></div>
                          ) : TheseWorkOrders.filter(
                              (ith) =>
                                ith.BrokerID === iith.BrokerID && ith.Active,
                            ).length === 0 ? (
                            <span className="NoWorkOrders">No Work Orders</span>
                          ) : (
                            <span className="WorkOrdersExist">
                              {TheseWorkOrders.filter(
                                (ith) =>
                                  ith.BrokerID === iith.BrokerID && ith.Active,
                              ).length.toString() +
                                " Work Orders currently running!"}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ),
                SortingValue:
                  TheseBrokers.filter(
                    (ith) =>
                      ith.BrokerageID === thisbrokerage.BrokerageID &&
                      ith.Active,
                  ).length +
                  TheseWorkOrders.filter(
                    (th) =>
                      TheseBrokers.filter(
                        (ith) =>
                          ith.BrokerageID === thisbrokerage.BrokerageID &&
                          ith.Active,
                      )
                        .map((iith) => iith.BrokerID)
                        .includes(th.BrokerID) && th.Active,
                  ).length,
              },
              Actions: {
                _TYPE: "Element",
                ElementValue: (
                  <>
                    {thisconveyance.Active ? (
                      <button
                        onClick={() => {
                          UpdateConveyanceLoadingMap(
                            thisconveyance.ConveyanceID,
                            true,
                          );
                          IDA.DisableConveyance(
                            thisconveyance.ConveyanceID,
                          ).then((res) => {
                            setTheseConveyances(
                              [...TheseConveyances]
                                .filter(
                                  (th) => th.ConveyanceID !== res.ConveyanceID,
                                )
                                .concat(res),
                            );
                          });
                        }}
                      >
                        <code>
                          DISABLE :: {thisconveyance.Name} :: ALL MACHINES
                        </code>
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          UpdateConveyanceLoadingMap(
                            thisconveyance.ConveyanceID,
                            true,
                          );
                          IDA.EnableConveyance(
                            thisconveyance.ConveyanceID,
                          ).then((res) => {
                            setTheseConveyances(
                              [...TheseConveyances]
                                .filter(
                                  (th) => th.ConveyanceID !== res.ConveyanceID,
                                )
                                .concat(res),
                            );
                          });
                        }}
                      >
                        <code>
                          ENABLE :: {thisconveyance.Name} :: ALL MACHINES
                        </code>
                      </button>
                    )}
                    {thisbrokerage.Active ? (
                      <button
                        onClick={() => {
                          UpdateBrokerageLoadingMap(
                            thisbrokerage.BrokerageID,
                            true,
                          );
                          IDA.DisableBrokerage(thisbrokerage.BrokerageID).then(
                            (res) => {
                              setTheseBrokerages(
                                [...TheseBrokerages]
                                  .filter(
                                    (th) => th.BrokerageID !== res.BrokerageID,
                                  )
                                  .concat(res),
                              );
                            },
                          );
                        }}
                      >
                        <code>
                          DISABLE :: <b>{thisconveyance.Name}</b> ::{" "}
                          <b>{thisbrokerage.MachineName}</b>
                        </code>
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          UpdateBrokerageLoadingMap(
                            thisbrokerage.BrokerageID,
                            true,
                          );
                          IDA.EnableBrokerage(thisbrokerage.BrokerageID).then(
                            (res) => {
                              console.log(res);
                              setTheseBrokerages(
                                [...TheseBrokerages]
                                  .filter(
                                    (th) => th.BrokerageID !== res.BrokerageID,
                                  )
                                  .concat(res),
                              );
                              UpdateBrokerageLoadingMap(
                                thisbrokerage.BrokerageID,
                                false,
                              );
                            },
                          );
                        }}
                      >
                        <code>
                          ENABLE :: <b>{thisconveyance.Name}</b> ::{" "}
                          <b>{thisbrokerage.MachineName}</b>
                        </code>
                      </button>
                    )}
                  </>
                ),
              },
              Status: {
                _TYPE: "Element",
                ElementValue: (
                  <div className="AllStatuses">
                    <div className="SubStatuses">
                      <code>
                        Conveyance ::{" "}
                        {thisconveyance.Active
                          ? EnabledCharm()
                          : DisabledCharm()}
                      </code>
                      <code>
                        Machine ::{" "}
                        {thisMachine.Active ? EnabledCharm() : DisabledCharm()}
                      </code>
                      <code>
                        Brokerage ::{" "}
                        {thisbrokerage.Active
                          ? EnabledCharm()
                          : DisabledCharm()}
                      </code>
                    </div>
                    <div className="OverallStatus">
                      <code>
                        Overall ::{" "}
                        {thisconveyance.Active &&
                        thisbrokerage.Active &&
                        thisMachine.Active
                          ? EnabledCharm()
                          : DisabledCharm()}
                      </code>
                    </div>
                  </div>
                ),
                SortingValue:
                  thisconveyance.Active &&
                  thisbrokerage.Active &&
                  thisMachine.Active
                    ? "ENABLED"
                    : "DISABLED",
                GroupingValue:
                  thisconveyance.Active &&
                  thisbrokerage.Active &&
                  thisMachine.Active
                    ? "ENABLED"
                    : "DISABLED",
              },
            });
          });
      });

      setStructuredConveyances(StructuredConveyances);
    }
  }, [
    TheseConveyances,
    TheseBrokerages,
    TheseBrokers,
    TheseWorkOrders,
    TheseMachines,
  ]);

  const EnabledCharm = () => {
    return <span className="EnabledCharm">ENABLED</span>;
  };

  const DisabledCharm = () => {
    return <span className="DisabledCharm">DISABLED</span>;
  };

  const [ShowGlossary, SetShowGlossary] = useState(true);

  return (
    <div className="InvarActivitySurface">
      <h1>Invar Activity</h1>
      <fieldset className="GlossaryPanel">
        <legend>
          Glossary{" "}
          <span
            className="CollapseToggle"
            onClick={() => {
              SetShowGlossary(!ShowGlossary);
            }}
          >
            {ShowGlossary ? "[-]" : "[+]"}
          </span>
        </legend>
        {ShowGlossary && (
          <table>
            <tbody>
              <tr>
                <th>Conveyance</th>
                <td>
                  An ordered list of automated tasks that form an automated
                  workflow.
                </td>
                <th>Enabled</th>
                <td>Should be running.</td>
              </tr>
              <tr>
                <th>Machine</th>
                <td>A physical device that runs Invar operations.</td>
                <th>Disabled</th>
                <td>Should not be running.</td>
              </tr>
              <tr>
                <th>Brokerage</th>
                <td>
                  A semantic mandate for a given Machine to run a given
                  Conveyance with a given Environment tag.
                </td>
                <th>Active</th>
                <td>Is running.</td>
              </tr>
              <tr>
                <th>Broker</th>
                <td>
                  The actual process that runs a given Conveyance on a given
                  Machine.
                </td>
                <th>Inactive</th>
                <td>Is not running.</td>
              </tr>
            </tbody>
          </table>
        )}
      </fieldset>
      <div className="MachineManager">
        <h2>Machinery</h2>
        <div className="MachineManagerExplanation">
          Invar has a presence on the Machines listed here. Use these controls
          to enable or disable them for system-wide operations.
        </div>
        <div className="TopMultiMachine">
          {MachinesLoading ? (
            <div className="LoadingPanel Block"></div>
          ) : (
            <table>
              <tr>
                <th rowSpan={2}>Machine:</th>
                {TheseMachines.map((th, i) => {
                  return (
                    <th
                      key={"MachineTitle_" + i.toString()}
                      className={
                        MachineLoadingMap[th.MachineID] === true
                          ? "LoadingPanel Cell"
                          : undefined
                      }
                    >
                      <span>{th.Name}</span>
                    </th>
                  );
                })}
              </tr>
              <tr>
                {TheseMachines.map((th, i) => {
                  return (
                    <td
                      key={"MachineShortDesc_" + i.toString()}
                      className={
                        MachineLoadingMap[th.MachineID] === true
                          ? "LoadingPanel Cell"
                          : undefined
                      }
                    >
                      <span>{th.ShortDesc}</span>
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th>Status:</th>
                {TheseMachines.map((th, i) => {
                  return (
                    <td
                      key={"MachineActiveFlag_" + i.toString()}
                      className={
                        MachineLoadingMap[th.MachineID] === true
                          ? "LoadingPanel Cell"
                          : undefined
                      }
                    >
                      <span>{th.Active === true ? "Enabled" : "Disabled"}</span>
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th>Activity:</th>
                {TheseMachines.map((th, i) => {
                  let AWOCount =
                    WorkOrdersLoading || BrokersLoading
                      ? 0
                      : TheseWorkOrders.filter(
                          (ith) =>
                            ith.Active &&
                            TheseBrokers.filter(
                              (iith) =>
                                iith.Active &&
                                th.Active &&
                                iith.MachineName === th.Name,
                            )
                              .map((th) => th.BrokerID)
                              .includes(ith.BrokerID),
                        ).length;

                  return (
                    <td
                      key={"MachineBrokerages_" + i.toString()}
                      className={
                        MachineLoadingMap[th.MachineID] === true
                          ? "LoadingPanel Cell"
                          : undefined
                      }
                    >
                      {
                        // Brokerages first, as they're more abstract.
                      }
                      {BrokeragesLoading ? (
                        <div className="LoadingPanel Block"></div>
                      ) : (
                        <>
                          <p>
                            {
                              TheseBrokerages.filter(
                                (ith) => ith.MachineName === th.Name,
                              ).length
                            }{" "}
                            Brokerages
                          </p>
                          <ul>
                            <li className="Enabled">
                              {
                                TheseBrokerages.filter(
                                  (ith) =>
                                    ith.MachineName === th.Name &&
                                    ith.Active === true,
                                ).length
                              }{" "}
                              Enabled
                            </li>
                            <li className="Disabled">
                              {
                                TheseBrokerages.filter(
                                  (ith) =>
                                    ith.MachineName === th.Name &&
                                    ith.Active === false,
                                ).length
                              }{" "}
                              Disabled
                            </li>
                          </ul>
                        </>
                      )}
                      {
                        // now Brokers as they're the specifics.
                      }
                      {BrokersLoading ? (
                        <div className="LoadingPanel Block"></div>
                      ) : (
                        <>
                          <p>
                            {
                              TheseBrokers.filter(
                                (ith) => ith.MachineName === th.Name,
                              ).length
                            }{" "}
                            Brokers
                          </p>

                          <ul>
                            <li className="Enabled">
                              {
                                TheseBrokers.filter(
                                  (ith) =>
                                    ith.MachineName === th.Name &&
                                    ith.Active === true,
                                ).length
                              }{" "}
                              Running
                              {props.Environment === "LOCAL" && (
                                <ul>
                                  {TheseBrokers.filter(
                                    (ith) =>
                                      ith.MachineName === th.Name &&
                                      ith.Active === true,
                                  ).map((ith) => {
                                    return (
                                      <li key={"brk_" + ith.BrokerID}>
                                        {ith.BrokerName}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </li>
                            <li className="Disabled">
                              {
                                TheseBrokers.filter(
                                  (ith) =>
                                    ith.MachineName === th.Name &&
                                    ith.Active === false,
                                ).length
                              }{" "}
                              Inactive
                              {props.Environment === "LOCAL" && (
                                <ul>
                                  {TheseBrokers.filter(
                                    (ith) =>
                                      ith.MachineName === th.Name &&
                                      ith.Active === false,
                                  ).map((ith) => {
                                    return (
                                      <li key={"brk_" + ith.BrokerID}>
                                        {ith.BrokerName}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </li>
                          </ul>
                        </>
                      )}
                      {
                        // now WorkOrders as we need to know which machines can be turned off safely.
                      }
                      {WorkOrdersLoading ? (
                        <div className="LoadingPanel Block"></div>
                      ) : (
                        <p className={AWOCount === 0 ? "Safe" : "Caution"}>
                          {AWOCount} Active Work Orders
                        </p>
                      )}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th>Actions:</th>
                {TheseMachines.map((th, i) => {
                  return (
                    <td
                      key={"MachineActions_" + i.toString()}
                      className={
                        MachineLoadingMap[th.MachineID] === true
                          ? "LoadingPanel Cell"
                          : undefined
                      }
                    >
                      {th.Active ? (
                        <button
                          onClick={() => {
                            UpdateMachineLoadingMap(th.MachineID, true);
                            IDA.DisableMachine(th.Name).then((res) => {
                              setTheseMachines(res);
                            });
                          }}
                        >
                          Disable
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            UpdateMachineLoadingMap(th.MachineID, true);
                            IDA.EnableMachine(th.Name).then((res) => {
                              setTheseMachines(res);
                            });
                          }}
                        >
                          Enable
                        </button>
                      )}
                      {
                        /* if zero active or inactive brokerages... */
                        !BrokeragesLoading &&
                          TheseBrokerages.filter(
                            (ith) =>
                              ith.MachineName === th.Name &&
                              ith.Active === true,
                          ).length === 0 &&
                          TheseBrokerages.filter(
                            (ith) =>
                              ith.MachineName === th.Name &&
                              ith.Active === false,
                          ).length === 0 && (
                            <button
                              onClick={() => {
                                setMachinesLoading(true);
                                IDA.DeleteMachine(th.Name).then((res) => {
                                  setTheseMachines(res);
                                });
                              }}
                            >
                              Delete
                            </button>
                          )
                      }
                      <button
                        onClick={() => {
                          if (
                            MondrianFilterDefaults !== undefined &&
                            MondrianFilterDefaults.Value === th.Name
                          ) {
                            SetMondrianFilterDefaults(undefined);
                          } else {
                            SetMondrianFilterDefaults({
                              MondrianColumn: {
                                HumanName: "Machine",
                                MachineName: "BMachine",
                                Sortable: false,
                                Groupable: false,
                                Filterable: true,
                                Hideable: false,
                              },
                              AllowWildcards: true,
                              MultiComma: false,
                              Value: th.Name,
                              Type: "Like",
                            });
                          }
                        }}
                      >
                        {MondrianFilterDefaults !== undefined &&
                        MondrianFilterDefaults.Value === th.Name
                          ? "Remove Filter"
                          : "Apply Filter"}
                      </button>
                    </td>
                  );
                })}
              </tr>
            </table>
          )}
        </div>
      </div>
      <div className="WideLoader">
        <div className="Desc">
          Broker and WorkOrder data is refreshed every 5 seconds, when this
          indicator blinks.
        </div>
      </div>
      {props.Environment === "LOCAL" &&
        (WorkOrdersLoading || WorkOrderDataLoading ? (
          <div className="LoadingPanel Block"></div>
        ) : TheseWorkOrders.length === 0 ? (
          <></>
        ) : (
          <WorkOrderTimeline
            StartDate={dayjs(
              TheseWorkOrders.sort((a, b) => {
                let check = dayjs(b.Created).diff(dayjs(a.Created));
                if (check === 0) {
                  return 0;
                } else if (check < 0) {
                  return -1;
                } else if (check > 0) {
                  return 1;
                }
              }).lastItem.Created,
            )}
            EndDate={dayjs(
              TheseWorkOrders.sort((a, b) => {
                let check = dayjs(b.Created).diff(dayjs(a.Created));
                if (check === 0) {
                  return 0;
                } else if (check < 0) {
                  return 1;
                } else if (check > 0) {
                  return -1;
                }
              }).lastItem.Created,
            ).add(15, "minute")}
            WorkOrders={TheseWorkOrders}
            WorkOrderData={TheseWorkOrderData}
          ></WorkOrderTimeline>
        ))}
      <h2>Activity</h2>
      {ConveyancesLoading ||
      BrokeragesLoading ||
      PassInSettings === undefined ? (
        <></>
      ) : (
        <Mondrian
          key={RenderKey}
          Name="InvarActivity"
          Caption="Current Activity of all Invar Conveyances, Brokerages, and Brokers."
          Debug={false}
          InputObjects={StructuredConveyances}
          LoadingFlag={ConveyancesLoading || BrokeragesLoading}
          Settings={PassInSettings}
          Columns={MondrianColumns}
        ></Mondrian>
      )}
    </div>
  );
};
